import {
  ActionPathPanel,
  ActionPathPanelWrap,
  BottomBar,
  TopBarWrapper,
  TopBar,
  Distance,
  CloseBtn,
  DistanceText,
  BottomBarText,
  BottomBarIcon,
  PlayButton,
  TeleportButton,
  BottomBarButtons,
  ButtonText,
  BottomBarInfo,
} from 'src/components/action-path-navbar/ActionPathNavbarInner.styled';
import { toast } from 'react-toastify';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { CloseBtnIcon } from 'src/components/action-path-navbar/svg/CloseBtnIcon';
import { PlayBtnIcon } from 'src/components/action-path-navbar/svg/PlayBtnIcon';
import { TeleportBtnIcon } from 'src/components/action-path-navbar/svg/TeleportBtnIcon';
import ThemeConfig from 'src/theme';
import { PauseBtnIcon } from 'src/components/action-path-navbar/svg/PauseBtnIcon';
import { useState, useMemo } from 'react';
import * as THREE from 'three';
import { CircleDot } from 'lucide-react';
import {
  getHorizontalRotationBetweenPoints,
  getVerticalRotationBetweenPoints,
} from 'src/utils/getRotation';
import { NavigationSlider } from './components/NavigationSlider';
import { TourType } from 'src/components/reel/types';
import { getQueryVariable } from 'src/utils/getVideoLink';
import { hexToRGBA, removeHTMLTagsFromString } from 'src/utils/all';
import { ITargetObjectData } from 'src/service/navigation/types';

const METERS_TO_FEET_CONVERSION = 3.28084;

const formatDistance = (
  distance: number | string,
  calculateDistanceIn: string | undefined,
  translation: any,
  rtl: boolean,
) => {
  const useFeet = calculateDistanceIn === 'ft';
  const metersVal = Number(distance);
  const value = useFeet ? (metersVal * METERS_TO_FEET_CONVERSION).toFixed(1) : distance;
  const unitText = useFeet
    ? translation.pathNavDistanceInFt || (rtl ? ' פיט להגעה ליעד' : 'ft to go')
    : translation.pathNavDistanceInM || (rtl ? ' מטר להגעה ליעד' : 'm to go');
  return `${value}${unitText}`;
};

export const ActionPathNavbarInner = ({
  tourType,
  pathFinderLine,
  setVisible,
  sdk,
  enableNavBar,
  destinationTitle,
  icon,
  autoNavigation,
  enableTeleportBtn,
  distance,
  playButtonType,
  setPlayButtonType,
  style,
  navigationMode,
  setActionPathFinished,
  setTeleportFinished,
  navigationSettings,
  webMode,
  rtl,
  sliderValue,
  setSliderValue,
  translation,
  resetNavigationByCloseButton,
  calculateDistanceIn,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const targetObjectData = pathFinderLine.targetObjectData || {};
  const autoNavigationPath = useMemo(
    () => pathFinderLine.getAutonavigationPath(),
    [pathFinderLine],
  );

  // Cache teleport points calculation to avoid recalculating on every click
  const allTeleportSweeps = useMemo(() => {
    const teleportPoints = pathFinderLine.getTeleportPoints();
    return teleportPoints.flatMap((tp) => tp.pointNodes?.map((pn) => pn.sweep) || []);
  }, [pathFinderLine]);

  const [sliderDisabled, setSliderDisabled] = useState(false);
  const hideNavbar =
    navigationMode &&
    enableNavBar &&
    !navigationSettings?.webContentEnabled &&
    !navigationSettings?.autoMoveButtonEnabled &&
    !navigationSettings?.teleportButtonEnabled;

  const step = useMemo(
    () => parseFloat((100 / autoNavigationPath.length).toFixed(0)),
    [autoNavigationPath],
  );

  const marks = useMemo(
    () =>
      autoNavigationPath.map((path, i) => ({
        value: i * step,
        id: path.data.sweepId || path.data.id,
      })),
    [autoNavigationPath, step],
  );

  const hasText =
    navigationMode && webMode
      ? navigationSettings?.webContentEnabled
      : targetObjectData.webPathContent && destinationTitle && destinationTitle !== '';
  const hasIcon =
    navigationMode && webMode
      ? navigationSettings?.webContentEnabled && navigationSettings?.webThumbnailEnabled
      : targetObjectData.webPathContent && targetObjectData.webPathThumbnail && icon;
  const hasButtonPlay = navigationMode ? navigationSettings?.autoMoveButtonEnabled : autoNavigation;
  const hasButtonTeleport = navigationMode
    ? navigationSettings?.teleportButtonEnabled
    : enableTeleportBtn;
  const buttonsExpanded =
    (!hasIcon && !hasText) ||
    (isMobile && hasText && hasIcon && hasButtonTeleport && hasButtonPlay);

  const bgColor =
    navigationSettings?.navbarBgColor && typeof navigationSettings?.navbarBgOpacity === 'number'
      ? hexToRGBA(navigationSettings?.navbarBgColor, navigationSettings?.navbarBgOpacity)
      : navigationSettings?.navbarBgColor;

  const moveToSweep = async (
    targetObjectData: ITargetObjectData,
    transition = sdk.Mattertag.Transition.FADEOUT,
  ) => {
    if (!sdk) {
      toast.error(translation.pleaseWaitSweeps || 'Wait for the model to load');
      return;
    }
    const sweepId = targetObjectData?.tag?.sid || targetObjectData?.id;

    const navigateToLocation = async () => {
      if (targetObjectData?.tag) {
        await sdk.Mattertag.navigateToTag(sweepId, transition).then(() => {
          setVisible(false);
          pathFinderLine.reset(true);
        });
      } else {
        await pathFinderLine.moveToSweep(sweepId, { x: 0, y: 0 }, 'FLY').then(async () => {
          await pathFinderLine.rotateCameraToPathDirection();
          setVisible(false);
          pathFinderLine.reset(true);
        });
      }
    };

    if (!pathFinderLine.inAutoNavigate) {
      return navigateToLocation();
    }

    pathFinderLine.inAutoNavigate = false;
    const intervalID = setInterval(() => {
      if (pathFinderLine.currentSweep?.uuid !== '') {
        window.clearInterval(intervalID);
        return navigateToLocation();
      }
      return;
    }, 250);
  };

  const handleSliderValueAutoChange = () => {
    setSliderValue((prevState) => {
      const nextStep = prevState + step;
      return nextStep;
    });
  };

  const handleNext = () => {
    if (sliderDisabled) return;

    // Check if next point is a teleport/elevator - if so, ignore play/pause to prevent crashes
    if (autoNavigationPath && autoNavigationPath.length > 0) {
      const currentSweepId = pathFinderLine.currentSweep?.uuid;
      const currentIndex = autoNavigationPath.findIndex(
        (point) => point.data.id === currentSweepId || point.data.sweepId === currentSweepId,
      );

      if (currentIndex >= 0 && currentIndex < autoNavigationPath.length - 1) {
        const nextPoint = autoNavigationPath[currentIndex + 1];
        if (nextPoint && allTeleportSweeps.includes(nextPoint.data.id)) {
          // Next point is a teleport/elevator - ignore this click to prevent crashes
          console.log('Ignoring play/pause - next point is elevator/teleport');
          return;
        }
      }
    }

    if (pathFinderLine.inAutoNavigate) {
      setPlayButtonType('Play');
      pathFinderLine.inAutoNavigate = false;
      return;
    }

    const url = window.location.href;
    const s = getQueryVariable(url, 's');

    if (!pathFinderLine.currentSweep?.uuid) {
      const intervalID = setInterval(() => {
        // Remove Kiosk && s check when MP fixes currentSweep to be set when tour has sweep id as query param
        if (pathFinderLine.currentSweep?.uuid || (tourType === TourType.Kiosk && s)) {
          clearInterval(intervalID);
          setPlayButtonType('Pause');
          pathFinderLine.autoNavigateCurrentPath(
            null,
            () => {},
            () => {
              pathFinderLine.inAutoNavigate = false;
              setPlayButtonType('Play');
            },
          );
        }
      }, 250);
      return;
    }

    setPlayButtonType('Pause');

    if (navigationMode) {
      pathFinderLine.autoNavigateCurrentPath(
        handleSliderValueAutoChange,
        () => {},
        () => {
          pathFinderLine.inAutoNavigate = false;
          setPlayButtonType('Play');
        },
      );
    } else {
      pathFinderLine.autoNavigateCurrentPath(
        null,
        () => {},
        () => {
          pathFinderLine.inAutoNavigate = false;
          setPlayButtonType('Play');
        },
      );
    }
  };

  const handleTeleport = async () => {
    await moveToSweep(targetObjectData);
    if (!pathFinderLine.inAutoNavigate && !!setTeleportFinished) {
      setTeleportFinished({ targetObjectData: { ...targetObjectData } });
    }
    // if (navigationMode) {
    //   setActionPathFinished(true);
    // }
  };

  const handleCloseButtonClick = () => {
    pathFinderLine.reset();
    setVisible(false);
    if (navigationMode) {
      resetNavigationByCloseButton(true);
    }
  };

  const handleSliderValueChange = async (_, newValue) => {
    const nextStep = marks.find((mark) => mark.value === newValue);
    const currentStep = marks.find((mark) => mark.value === sliderValue);

    if (nextStep?.value === currentStep?.value || pathFinderLine.inAutoNavigate) return;

    setSliderValue(newValue);
    setSliderDisabled(true);

    const currentSweep = autoNavigationPath.find((path) => path.data.sweepId === currentStep.id);
    const nextSweep = autoNavigationPath.find((path) => path.data.sweepId === nextStep.id);
    const nextSweepIndex = autoNavigationPath.findIndex(
      (path) => path.data.sweepId === nextStep.id,
    );

    const sweepAfterNext =
      nextSweepIndex >= 0 && nextSweepIndex < autoNavigationPath.length - 1
        ? autoNavigationPath[nextSweepIndex + 1]
        : null;

    const pos1 = new THREE.Vector3(
      currentSweep.data.position.x,
      currentSweep.data.position.y - 1.15,
      currentSweep.data.position.z,
    );
    const pos2 = new THREE.Vector3(
      nextSweep.data.position.x,
      nextSweep.data.position.y - 1.15,
      nextSweep.data.position.z,
    );

    const pos3 = sweepAfterNext
      ? new THREE.Vector3(
          sweepAfterNext.data.position.x,
          sweepAfterNext.data.position.y - 1.15,
          sweepAfterNext.data.position.z,
        )
      : null;

    const yawDegrees =
      nextStep.value > currentStep.value
        ? pos3
          ? getHorizontalRotationBetweenPoints(pos2, pos3)
          : getHorizontalRotationBetweenPoints(pos1, pos2)
        : getHorizontalRotationBetweenPoints(pos2, pos1);

    const pitchDegrees =
      nextStep.value > currentStep.value
        ? pos3
          ? getVerticalRotationBetweenPoints(pos2, pos3) * 0.75
          : getVerticalRotationBetweenPoints(pos1, pos2) * 0.75
        : getVerticalRotationBetweenPoints(pos2, pos1) * 0.75;

    if (nextStep.value === marks[marks.length - 1].value) {
      await moveToSweep(targetObjectData);
      setActionPathFinished(true);
      setSliderDisabled(false);
    } else {
      await pathFinderLine.moveToSweep(nextStep.id, { x: pitchDegrees, y: yawDegrees }, 'INSTANT');
      setSliderDisabled(false);
    }
  };

  if (
    (!targetObjectData.enableNavBar &&
      !targetObjectData.enableTeleportBtn &&
      !targetObjectData.autoNavigation) ||
    hideNavbar
  ) {
    return null;
  }

  return (
    <ThemeConfig>
      <ActionPathPanelWrap style={style} rtl={rtl}>
        <ActionPathPanel navigationMode={navigationMode}>
          <TopBarWrapper>
            <TopBar bgColor={bgColor}>
              <Distance rtl={rtl}>
                <DistanceText textColor={navigationSettings?.navbarTextColor}>
                  {formatDistance(distance, calculateDistanceIn, translation, rtl)}
                </DistanceText>
              </Distance>
              <CloseBtn onClick={handleCloseButtonClick}>
                <CloseBtnIcon />
              </CloseBtn>
            </TopBar>
          </TopBarWrapper>
          <BottomBar
            vertical={isMobile && hasIcon && hasText && hasButtonPlay && hasButtonTeleport}
            navigationMode={navigationMode}
            bgColor={bgColor}
            rtl={rtl}
          >
            {(hasText || hasIcon) && (
              <BottomBarInfo
                grow={!hasIcon && !hasButtonPlay && !hasButtonTeleport}
                expanded={isMobile && buttonsExpanded}
              >
                {hasIcon &&
                  (targetObjectData.navigationThumbnail ? (
                    <BottomBarIcon
                      src={`${process.env.NEXT_PUBLIC_CDN}/tours/${targetObjectData.tourId}/tags/icons/${targetObjectData.navigationThumbnail}`}
                    />
                  ) : icon ? (
                    <BottomBarIcon src={icon} />
                  ) : (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      width="40px"
                      height="40px"
                      sx={{
                        borderRadius: '9px',
                        border: '1px solid #E8E8E8',
                        color: '#fff',
                        background: 'transparent',
                      }}
                    >
                      <CircleDot
                        size={30}
                        color="#fff"
                      />
                    </Stack>
                  ))}
                <Stack gap="4px">
                  {hasText && (
                    <BottomBarText
                      alignText={
                        !hasIcon && !hasButtonPlay && !hasButtonTeleport ? 'center' : 'left'
                      }
                      flexGrow={!hasIcon && !hasButtonPlay && !hasButtonTeleport ? 1 : 0}
                      textColor={navigationSettings?.navbarTextColor}
                    >
                      {destinationTitle}
                    </BottomBarText>
                  )}
                  {targetObjectData.webPathDescriptionEnabled && targetObjectData?.description && (
                    <BottomBarText
                      alignText={
                        !hasIcon && !hasButtonPlay && !hasButtonTeleport ? 'center' : 'left'
                      }
                      flexGrow={!hasIcon && !hasButtonPlay && !hasButtonTeleport ? 1 : 0}
                      textColor={navigationSettings?.navbarTextColor}
                      descriptionText
                    >
                      {removeHTMLTagsFromString(targetObjectData.description)}
                    </BottomBarText>
                  )}
                </Stack>
              </BottomBarInfo>
            )}
            {(hasButtonPlay || hasButtonTeleport) && (
              <BottomBarButtons
                rtl={rtl}
                expanded={buttonsExpanded}
                marginLeft={0}
                marginTop={isMobile && buttonsExpanded && (hasText || hasIcon) ? '10px' : 0}
              >
                {hasButtonPlay && (
                  <PlayButton
                    bg={theme.palette.treedis.blue.main}
                    expanded={buttonsExpanded}
                    onClick={handleNext}
                    rtl={rtl}
                  >
                    {playButtonType === 'Pause' ? <PauseBtnIcon /> : <PlayBtnIcon />}
                    {buttonsExpanded && (
                      <ButtonText color="white">
                        {translation.pathNavAutoPlay || (rtl ? 'ניווט אוטמטי' : 'Auto Play')}
                      </ButtonText>
                    )}
                  </PlayButton>
                )}
                {hasButtonTeleport && (
                  <TeleportButton rtl={rtl} expanded={buttonsExpanded} onClick={handleTeleport}>
                    <TeleportBtnIcon />
                    {buttonsExpanded && (
                      <ButtonText color={theme.palette.treedis.blue.main}>
                        {translation.pathNavTeleport || (rtl ? 'דילוג ליעד' : 'Teleport')}
                      </ButtonText>
                    )}
                  </TeleportButton>
                )}
              </BottomBarButtons>
            )}
          </BottomBar>
          {navigationMode && (
            <NavigationSlider
              disabled={!!pathFinderLine.inAutoNavigate || sliderDisabled}
              handleSliderValueChange={handleSliderValueChange}
              marks={marks}
              sliderValue={sliderValue}
              step={step}
            />
          )}
        </ActionPathPanel>
      </ActionPathPanelWrap>
    </ThemeConfig>
  );
};
